import {
  type KeyboardEvent as ReactKeyboardEvent,
  type PointerEvent as ReactPointerEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";

/** Namespaced preference for the foreclosure details dialog size. */
export const FORECLOSURE_DETAILS_DIALOG_SIZE_STORAGE_KEY =
  "hera.attomForeclosureDetailsDialog.size";

/** Matches the previous fixed caps: min(96vw, 1400px) by min(92vh, 960px). */
export const FORECLOSURE_DETAILS_DIALOG_DEFAULT_WIDTH = 1400;
export const FORECLOSURE_DETAILS_DIALOG_DEFAULT_HEIGHT = 960;
/** Keeps the header, Previous/Next, and a slice of the body usable. */
export const FORECLOSURE_DETAILS_DIALOG_MIN_WIDTH = 760;
export const FORECLOSURE_DETAILS_DIALOG_MIN_HEIGHT = 480;
export const FORECLOSURE_DETAILS_DIALOG_MAX_WIDTH_RATIO = 0.96;
export const FORECLOSURE_DETAILS_DIALOG_MAX_HEIGHT_RATIO = 0.92;

const KEYBOARD_RESIZE_STEP = 24;
const KEYBOARD_RESIZE_STEP_LARGE = 80;

export type ForeclosureDetailsDialogSize = {
  width: number;
  height: number;
};

const SERVER_VIEWPORT_KEY = "1600x1200";

let cachedViewportWidth = -1;
let cachedViewportHeight = -1;
let cachedViewportKey = SERVER_VIEWPORT_KEY;

function subscribeToViewport(onChange: () => void): () => void {
  window.addEventListener("resize", onChange);
  return () => window.removeEventListener("resize", onChange);
}

function getViewportSnapshot(): string {
  const width = window.innerWidth;
  const height = window.innerHeight;
  if (width !== cachedViewportWidth || height !== cachedViewportHeight) {
    cachedViewportWidth = width;
    cachedViewportHeight = height;
    cachedViewportKey = `${width}x${height}`;
  }
  return cachedViewportKey;
}

function getViewportServerSnapshot(): string {
  return SERVER_VIEWPORT_KEY;
}

function parseViewportKey(key: string): ForeclosureDetailsDialogSize {
  const [width, height] = key.split("x");
  const parsedWidth = Number(width);
  const parsedHeight = Number(height);
  if (
    !Number.isFinite(parsedWidth) ||
    !Number.isFinite(parsedHeight) ||
    parsedWidth <= 0 ||
    parsedHeight <= 0
  ) {
    return {
      width: FORECLOSURE_DETAILS_DIALOG_DEFAULT_WIDTH,
      height: FORECLOSURE_DETAILS_DIALOG_DEFAULT_HEIGHT,
    };
  }
  return { width: parsedWidth, height: parsedHeight };
}

function clampAxis(value: number, min: number, max: number): number {
  const rounded = Number.isFinite(value) ? Math.round(value) : min;
  return Math.min(max, Math.max(min, rounded));
}

/** Shrink to the viewport caps (96vw / 92vh) and keep the usable minimum. */
export function clampForeclosureDetailsDialogSize(
  width: number,
  height: number,
  viewportWidth: number,
  viewportHeight: number,
): ForeclosureDetailsDialogSize {
  const maxWidth = Math.max(
    1,
    Math.floor(viewportWidth * FORECLOSURE_DETAILS_DIALOG_MAX_WIDTH_RATIO),
  );
  const maxHeight = Math.max(
    1,
    Math.floor(viewportHeight * FORECLOSURE_DETAILS_DIALOG_MAX_HEIGHT_RATIO),
  );
  const minWidth = Math.min(FORECLOSURE_DETAILS_DIALOG_MIN_WIDTH, maxWidth);
  const minHeight = Math.min(FORECLOSURE_DETAILS_DIALOG_MIN_HEIGHT, maxHeight);
  return {
    width: clampAxis(width, minWidth, maxWidth),
    height: clampAxis(height, minHeight, maxHeight),
  };
}

export function readForeclosureDetailsDialogSize(): ForeclosureDetailsDialogSize {
  const fallback = {
    width: FORECLOSURE_DETAILS_DIALOG_DEFAULT_WIDTH,
    height: FORECLOSURE_DETAILS_DIALOG_DEFAULT_HEIGHT,
  };
  if (typeof window === "undefined") return fallback;
  try {
    const raw = window.localStorage.getItem(
      FORECLOSURE_DETAILS_DIALOG_SIZE_STORAGE_KEY,
    );
    if (!raw) return fallback;
    const parsed = JSON.parse(raw) as { width?: unknown; height?: unknown };
    const width = Number(parsed?.width);
    const height = Number(parsed?.height);
    if (
      !Number.isFinite(width) ||
      !Number.isFinite(height) ||
      width <= 0 ||
      height <= 0
    ) {
      return fallback;
    }
    return { width, height };
  } catch {
    return fallback;
  }
}

export function writeForeclosureDetailsDialogSize(
  size: ForeclosureDetailsDialogSize,
): void {
  try {
    window.localStorage.setItem(
      FORECLOSURE_DETAILS_DIALOG_SIZE_STORAGE_KEY,
      JSON.stringify({ width: size.width, height: size.height }),
    );
  } catch {
    // Storage may be unavailable; keep the size for this mount.
  }
}

export function useForeclosureDetailsDialogSize() {
  const viewportKey = useSyncExternalStore(
    subscribeToViewport,
    getViewportSnapshot,
    getViewportServerSnapshot,
  );
  const viewport = parseViewportKey(viewportKey);
  const [preferred, setPreferred] = useState(readForeclosureDetailsDialogSize);
  const preferredRef = useRef(preferred);
  const dragCleanupRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    return () => {
      dragCleanupRef.current?.();
    };
  }, []);

  const size = clampForeclosureDetailsDialogSize(
    preferred.width,
    preferred.height,
    viewport.width,
    viewport.height,
  );

  const onResizePointerDown = useCallback(
    (event: ReactPointerEvent<HTMLElement>) => {
      if (event.button !== 0) return;
      event.preventDefault();
      event.stopPropagation();
      event.currentTarget.focus();
      event.currentTarget.setPointerCapture(event.pointerId);

      const pointerId = event.pointerId;
      const originX = event.clientX;
      const originY = event.clientY;
      const originalPreferred = preferredRef.current;
      const origin = clampForeclosureDetailsDialogSize(
        originalPreferred.width,
        originalPreferred.height,
        window.innerWidth,
        window.innerHeight,
      );
      let latest = origin;
      let moved = false;

      const onMove = (moveEvent: PointerEvent) => {
        if (moveEvent.pointerId !== pointerId) return;
        moved = true;
        latest = clampForeclosureDetailsDialogSize(
          origin.width + (moveEvent.clientX - originX),
          origin.height + (moveEvent.clientY - originY),
          window.innerWidth,
          window.innerHeight,
        );
        preferredRef.current = latest;
        setPreferred(latest);
      };
      const finish = () => {
        window.removeEventListener("pointermove", onMove);
        window.removeEventListener("pointerup", onUp);
        window.removeEventListener("pointercancel", onUp);
        dragCleanupRef.current = null;
      };
      const onUp = (upEvent: PointerEvent) => {
        if (upEvent.pointerId !== pointerId) return;
        finish();
        const unchanged =
          !moved ||
          (latest.width === origin.width && latest.height === origin.height);
        if (unchanged) {
          preferredRef.current = originalPreferred;
          setPreferred(originalPreferred);
          return;
        }
        preferredRef.current = latest;
        setPreferred(latest);
        writeForeclosureDetailsDialogSize(latest);
      };

      dragCleanupRef.current?.();
      dragCleanupRef.current = finish;
      window.addEventListener("pointermove", onMove);
      window.addEventListener("pointerup", onUp);
      window.addEventListener("pointercancel", onUp);
    },
    [],
  );

  const onResizeKeyDown = useCallback(
    (event: ReactKeyboardEvent<HTMLElement>) => {
      const step = event.shiftKey
        ? KEYBOARD_RESIZE_STEP_LARGE
        : KEYBOARD_RESIZE_STEP;
      let widthDelta = 0;
      let heightDelta = 0;
      switch (event.key) {
        case "ArrowRight":
          widthDelta = step;
          break;
        case "ArrowLeft":
          widthDelta = -step;
          break;
        case "ArrowDown":
          heightDelta = step;
          break;
        case "ArrowUp":
          heightDelta = -step;
          break;
        default:
          return;
      }
      event.preventDefault();
      const current = clampForeclosureDetailsDialogSize(
        preferredRef.current.width,
        preferredRef.current.height,
        window.innerWidth,
        window.innerHeight,
      );
      const next = clampForeclosureDetailsDialogSize(
        current.width + widthDelta,
        current.height + heightDelta,
        window.innerWidth,
        window.innerHeight,
      );
      if (next.width === current.width && next.height === current.height) {
        return;
      }
      preferredRef.current = next;
      setPreferred(next);
      writeForeclosureDetailsDialogSize(next);
    },
    [],
  );

  return { size, onResizePointerDown, onResizeKeyDown };
}
